import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Typography } from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { useTranslation } from 'react-i18next';
import { TIMEOUT_DELAY, TIMEOUT_ARE_YOU_SURE } from '../constants/globals';
import { COMPANY_ID } from '../constants/kiosk_company_code_types';
import { MESSAGE as QUESTION_TYPE_MESSAGE } from '../constants/question_types';
import { createKioskUriWithQueryParams, createSubmitUriWithQueryParams } from '../helpers/link_helper';
import { isValidAnswer, hasAnsweredValue } from '../helpers/answer_helper';
import { hasMatchedCondition, hasMoreVisibleQuestion } from '../helpers/logic_helper';
import { selectHasMetSurveyRequirements } from '../redux/selectors/survey_selectors';
import idleTimeout from '../helpers/idle_helper';
import {
  submitAnswer,
  gotoNextQuestion,
  gotoPrevQuestion,
  resetApp,
  fetchCompany,
  gotoQuestion,
  gotoSubmitPage,
} from '../redux/actions';
import Alert from '../components/Alert';
import ProgressBar from '../components/ProgressBar';
import Question from '../components/Question';
import SurveyNav from '../components/SurveyNav';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';
import { SURVEY_THANKYOU_ROUTE } from '../constants/routes';
import { useRouter } from '../hooks/useRouter';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

// Single idleTimer used within closure for shared timers between instances.
const idleTimer = idleTimeout();

const useStyles = makeStyles((theme) => ({
  root: theme.defaultLayout.root,
  carousel: {
    overflow: 'auto',
    display: 'block',
    height: '100%',
  },
  footer: {
    display: 'block',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#f5f5f5',
    height: theme.measurements.footerHeight,
    padding: '18px 24px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      height: theme.measurements.smallScreenFooterHeight,
    },
  },
  progressContainer: {
    width: 200,
    [theme.breakpoints.down('lg')]: {
      width: 130,
    },
    '@media (max-width: 299.95px)': {
      width: '100%',
    },
  },
  navContainer: {
    minWidth: '170px',
  },
  progressBarContainer: {
    backgroundColor: '#B8B8B8',
    height: 10,
    width: '100%',
    borderRadius: theme.spacing(0.5),
  },
  totalText: {
    fontSize: 14,
    [theme.breakpoints.down('lg')]: {
      fontSize: 12,
    },
  },
  submitStatus: {
    position: 'absolute',
    left: 0,
    bottom: '2rem',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xl')]: {
      bottom: '2rem',
      padding: theme.spacing(3),
    },
  },
  submitStatusText: {
    color: '#4f4f4f',
    fontSize: 12,
  },
  swipeView: {
    height: '100%',
  },
  languageBox: {
    position: 'relative',
    [theme.breakpoints.down('xl')]: {
      display: 'block',
      position: 'unset',
      padding: theme.spacing(1),
    },
  },
  link: {
    textDecoration: 'none',
    textTransform: 'none',
    color: '#0F7B85',
  },
  anonymityTextContainer: {
    display: 'block',
    position: 'fixed',
    right: 0,
    bottom: '3.5rem',
    color: 'grey',
    padding: '0 2.5rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0 1.5rem',
      bottom: '7rem',
    },
  },
  anonymityTextLink: {
    textDecoration: 'underline',
  },
  anonymityText: {
    color: 'black',
  },
}));

const SurveyPage = () => {
  const styles = useStyles();
  const { survey_code: surveyCode = null } = useParams(); // `survey_code`comes from route defined in `App.js`
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const router = useRouter({ keepQueryParams: true });

  const createKioskUri = createKioskUriWithQueryParams(location.search);
  const createSubmitUri = createSubmitUriWithQueryParams(location.search, surveyCode);
  const titleRefs = useRef({});

  const [errorMsg, setErrorMsg] = useState('');
  const [confirmingAnswer, setConfirmingAnswer] = useState(null);
  const [showAnonymityDialog, setShowAnonymityDialog] = useState(false);

  const { isKiosk } = useAppSelector((state) => state.global);
  const { selectedLanguage } = useAppSelector((state) => state.languages);
  const {
    isSaving: surveyIsSaving,
    currentQuestionIndex,
    companyId,
    isSubmitted,
    showSubmitPage,
    name,
    anonymityThreshold,
  } = useAppSelector((state) => state.survey);
  const { kioskId } = useAppSelector((state) => state.company);

  const EFFECT_DELAY_TIMEOUT = 500;

  const questions = useAppSelector((state) => state.questions);
  const answers = useAppSelector((state) => state.answers);

  const hasMetSurveyRequirements = useAppSelector(selectHasMetSurveyRequirements);
  const [timeoutAlertIsVisible, setTimeoutAlertVisibility] = useState(false);
  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : null;
  const isCurrentAnswerSubmitted = currentQuestion && (!currentQuestion.isRequired || currentAnswer?.isSubmitted);
  const [comment, setComment] = useState(answers[currentQuestion?.id]?.comment || '');

  const currentAnswerIsValid =
    currentQuestion && isValidAnswer(currentQuestion, currentAnswer) && isCurrentAnswerSubmitted;

  const visibleQuestions = questions.filter((question) => {
    if (!question.displayConditions || question.displayConditions.length <= 0) return true;
    return hasMatchedCondition(question.displayConditions, answers);
  });

  const visibleIndex = visibleQuestions.findIndex((q) => q.id === currentQuestion.id);
  const visibleCount = visibleQuestions.length;
  const currentPosition = visibleIndex + 1;
  const progress = (currentPosition / visibleCount) * 100;

  const isLastQuestion = !hasMoreVisibleQuestion(currentQuestionIndex, questions, answers);
  const isAnonymized = anonymityThreshold > 1;

  useEffect(() => {
    dispatch(fetchCompany(companyId, COMPANY_ID));
    // eslint-disable-next-line
  }, [companyId]);

  useEffect(() => {
    if (showSubmitPage && !isSubmitted) navigate(createSubmitUri());
    // eslint-disable-next-line
  }, [showSubmitPage, isSubmitted]);

  useEffect(() => {
    if (isSubmitted) router.goTo(SURVEY_THANKYOU_ROUTE, [surveyCode]);
    // eslint-disable-next-line
  }, [isSubmitted]);

  // Focus keyboard on screen-reader announcement whenever question changes.
  useEffect(() => {
    if (titleRefs?.current && titleRefs.current[currentQuestion?.id]) {
      // Must focus after the swipe component finish animation, otherwise location will be messed up
      setTimeout(() => {
        // announceRefs.current[currentQuestion.id] could disappear after the delay
        if (titleRefs.current[currentQuestion.id]) titleRefs.current[currentQuestion.id].focus();
      }, EFFECT_DELAY_TIMEOUT);
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (currentAnswer) setComment(currentAnswer.comment);
  }, [currentAnswer]);

  // Idle Timeout Handlers
  // ---------------------
  // Reset application and redirect to kiosk login.
  const onAreYouSureIdle = () => {
    console.log('Timed out: resetting Kiosk');
    idleTimer.stop();
    dispatch(resetApp()); // Reset app for security (e.g., if someone else visits kiosk).
    navigate(createKioskUri(kioskId));
  };

  // Set secondary timeout while "are you sure?" modal is displayed.
  // Returns to normal timeout if user clicks "yes", timer reset when mouse moves.
  const onIdle = () => {
    idleTimer.restart({
      delay: TIMEOUT_ARE_YOU_SURE,
      onTimeout: onAreYouSureIdle,
    });
    setTimeoutAlertVisibility(true);
  };

  // Set normal timeout while using app.
  const resetIdleTimer = () => {
    idleTimer.restart({
      delay: TIMEOUT_DELAY,
      onTimeout: onIdle,
    });
    setTimeoutAlertVisibility(false);
  };

  // Initialize idle timer when component loads (only if in kiosk-mode).
  useEffect(() => {
    if (isKiosk) resetIdleTimer();

    return () => idleTimer.stop();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    document.title = t('docTitle.surveyName', { name });
    return () => {
      // Temp use a clean up function here before declare specific titles for each page
      document.title = 'WorkTango Pulse';
    };
  }, [name, t]);

  const createScreenReaderLabel = (question, lang) => {
    const questionLang = question.languages[lang];

    if (question.type === QUESTION_TYPE_MESSAGE) {
      return `${t('survey.navigation.screen-reader-message')}, ${questionLang.description}`;
    }

    const pageIndex = t('survey.navigation.screen-reader-label', {
      questionIndex: currentPosition,
      questionCount: visibleCount,
    });

    return `${pageIndex}, ${questionLang.title}`;
  };

  const onClickNext = (e) => {
    const isValidTarget = e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'LI';
    if (!isValidTarget) return;
    if (e) e.preventDefault();
    if (!currentAnswerIsValid) return;

    // Need prevent user going to next question if they haven't answered the question but put comment
    const hasAnswer = hasAnsweredValue(currentAnswer);
    if (!hasAnswer && !!comment) return;

    if (currentAnswer?.comment !== comment) {
      dispatch(
        submitAnswer(currentQuestion, {
          ...currentAnswer,
          comment,
        }),
      );
    }
    if (hasMetSurveyRequirements && isLastQuestion) {
      dispatch(gotoSubmitPage());
      return;
    }
    dispatch(gotoNextQuestion(currentQuestion.id));
  };

  const onClickPrev = (e) => {
    const isValidTarget = e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'LI';
    if (!isValidTarget) return;

    if (e) e.preventDefault();
    if (currentAnswer?.comment !== comment) {
      dispatch(
        submitAnswer(currentQuestion, {
          ...currentAnswer,
          comment,
        }),
      );
    }
    dispatch(gotoPrevQuestion(currentQuestion.id));
  };

  const handleChooseAnswer =
    (question, questions, answers) =>
    async (answer, gotoNext = true) => {
      const index = questions.findIndex((q) => q.id === question.id);

      // Only execute if this has been triggered by the currently active question.
      if (currentQuestionIndex !== index || errorMsg) return;

      // Currently, treat all questions after as dependents
      const hasAnswerAfterQuestion = !!questions.find((q, questionIndex) => {
        if (questionIndex <= index) return false;
        return !!answers[q.id];
      });

      const shouldCleanDependents = hasAnswerAfterQuestion && question.controlConditions?.length > 0;

      if (shouldCleanDependents) {
        setConfirmingAnswer({
          question,
          answer,
          gotoNext,
        });
      } else {
        handleAnswerSelected(question, answer, gotoNext);
      }
    };

  const handleCancelAnswer = () => {
    setConfirmingAnswer(null);
  };

  const handleConfirmAnswer = () => {
    handleAnswerSelected(confirmingAnswer.question, confirmingAnswer.answer, confirmingAnswer.gotoNext);
  };

  // 1. choose answer, no comments => submitAnswer + gotoNext
  // 2. choose answer, with comments => submitAnswer
  // 3. choose skip, no comments => submitAnswer + gotoNext
  // 4. choose skip, with comments => gotoNext
  // 5. previously saved answer + choose skip with comments => gotoNext
  // NOTE: This event handler is also executed when clicking the "skip" button, but
  // in that case, the `answer` is "empty" (e.g., looks like `{ value: null, comment: '' }`).
  const handleAnswerSelected = async (question, answer, gotoNext = true) => {
    // Do not check shouldSubmit for now as survey component is not able to handle this properly yet
    // We need to pass this as disabled prop to survey component, so the answer state will not be changed if we are sending request
    // const shouldSubmit = !isSubmittingAnswer || question.type === 'OPEN';

    // NOTE: Making an assumption here that when a question is "answered" but contains
    // not user input value, this is interpreted to mean it was "skipped".
    // `value` is defined in the reusable-survey-components repo and could be
    // either null, empty-string, or empty-array if not defined.
    const isSkipped = !hasAnsweredValue(answer);

    const result = await dispatch(
      submitAnswer(question, {
        ...answer,
        isSkipped,
      }),
    );

    setConfirmingAnswer(null);

    if (!result.isSuccess && !result.hideErrorMessage) {
      // Put this outside setTimeout to make sure no further submission will be triggered
      setErrorMsg(result.error.message || t('error.unknow'));
    }

    setTimeout(() => {
      if (!result.isSuccess) {
        // Put this inside setTimeout to make sure it not being override by a previous success submission
        // make sure user stay in current question if there's submission error
        dispatch(gotoQuestion(question.id));
        return;
      }

      // If this is the last question in the survey, go to the submit page,
      // otherwise, go to the next question (if gotoNext is true).
      // Is last question will be changed based on selections and logic, so should always recalculate it
      const newAnswers = {
        ...answers,
        [question.id]: answer,
      };

      const isLast = !hasMoreVisibleQuestion(currentQuestionIndex, questions, newAnswers);
      if (isLast && gotoNext) {
        dispatch(gotoSubmitPage());
      } else if (gotoNext) {
        dispatch(gotoNextQuestion(question.id));
      }
    }, EFFECT_DELAY_TIMEOUT);
  };

  const onSwipe = (newIndex, lastIndex, _meta) => {
    if (newIndex > lastIndex) {
      dispatch(gotoNextQuestion(currentQuestion.id));
    } else {
      dispatch(gotoPrevQuestion(currentQuestion.id));
    }
  };

  // 1. if no answers yet submitted, display nothing'
  // 2. if at least 1 answer && current saving, display "Saving..."
  // 3. if at least 1 answer && not current saving, display "Saved"
  const renderSaveStatus = () => {
    const hasAnswers = Object.keys(answers).length !== 0;
    if (!hasAnswers) return null;

    const text = surveyIsSaving ? `${t('saving')}...` : t('saved');

    return (
      <Typography className={styles.submitStatusText} role={hasAnswers && !surveyIsSaving ? 'alert' : undefined}>
        {text}
      </Typography>
    );
  };

  const handleClearError = () => {
    setErrorMsg('');
  };

  const handleCommentChange = (comment) => {
    setComment(comment);
  };

  const handleClickAnonymityLink = () => {
    setShowAnonymityDialog(true);
  };

  const handleCancleAnonymityDialog = () => {
    setShowAnonymityDialog(false);
  };

  const genAnonymityText = (t) => {
    if (!anonymityThreshold) return null;

    if (isAnonymized) {
      return (
        <Typography onClick={handleClickAnonymityLink} className={styles.anonymityTextLink}>
          {t('survey.anonymity-text.anonymized')}
        </Typography>
      );
    }
    return (
      <Typography>
        {t('survey.anonymity-text.not-anonymized')}
        <span onClick={handleClickAnonymityLink} className={styles.anonymityTextLink}>
          {t('survey.anonymity-text.details-here')}
        </span>
      </Typography>
    );
  };

  useKeyboardNavigation(onClickNext, onClickPrev);

  return (
    <div className={styles.root} role="main" id="main">
      <Alert
        isVisible={timeoutAlertIsVisible}
        title={t('swal.timeout.header')}
        okLabel={t('swal.confirm.yes')}
        onClickOK={resetIdleTimer}
      >
        {t('survey.timeout-warning', {
          timeout: Math.round(TIMEOUT_ARE_YOU_SURE / 1000),
        })}
      </Alert>

      <Alert
        isVisible={!!confirmingAnswer}
        title={t('logic.cleanup.warning')}
        okLabel={t('swal.confirm.yes')}
        onClickOK={handleConfirmAnswer}
        cancelLabel={t('swal.confirm.cancel')}
        onClickCancel={handleCancelAnswer}
      >
        {t('logic.cleanup')}
      </Alert>

      <Alert
        isVisible={!!errorMsg}
        title={t('error.submission')}
        okLabel={t('swal.confirm.yes')}
        onClickOK={handleClearError}
      >
        {errorMsg}
      </Alert>

      <Alert
        isVisible={showAnonymityDialog}
        title={t('anonymity-dialog.title')}
        okLabel={t('button.dismiss')}
        onClickOK={handleCancleAnonymityDialog}
      >
        {isAnonymized ? (
          <div className={styles.anonymityText}>
            {t('anonymity-dialog.content.anonymized.one')}
            <br />
            <br />
            <b>{t('anonymity-dialog.content.anonymized.two', { threshold: anonymityThreshold })}</b>
          </div>
        ) : (
          <div className={styles.anonymityText}>
            {t('anonymity-dialog.content.not-anonymized.one')}
            <br />
            <br />
            {t('anonymity-dialog.content.not-anonymized.two')}
            <br />
            <br />
            {t('anonymity-dialog.content.not-anonymized.three')}
          </div>
        )}
      </Alert>

      <article className={styles.carousel}>
        {selectedLanguage && visibleQuestions.length && (
          <SwipeableViews
            axis="y"
            enableMouseEvents
            index={visibleIndex}
            onChangeIndex={onSwipe}
            className={styles.swipeView}
            containerStyle={{ height: '100%' }}
            slideStyle={{ height: '100%' }}
            disabled
          >
            {visibleQuestions.map((question) => (
              <Question
                titleRefs={titleRefs}
                key={question.id}
                lang={selectedLanguage.langISOCode}
                question={question}
                isActive={question.id === currentQuestion.id}
                answer={answers[question.id] || null}
                screenReaderLabel={createScreenReaderLabel(question, selectedLanguage.langISOCode)}
                onChooseAnswer={handleChooseAnswer(question, questions, answers)}
                onCommentChange={handleCommentChange}
              />
            ))}
          </SwipeableViews>
        )}
      </article>

      <div className={styles.anonymityTextContainer}>{genAnonymityText(t)}</div>

      <footer className={styles.footer}>
        <div className={styles.submitStatus} role="complementary">
          {renderSaveStatus()}
        </div>

        <Grid
          container
          direction={{ sm: 'row-reverse' }}
          spacing={{ xs: 1 }}
          justifyContent={{ xs: 'space-between', sm: 'initial' }}
        >
          <Grid item xs={12} sm="auto" container direction="row" justifyContent="space-between" spacing={1}>
            {visibleCount > 0 && (
              <Grid xs={12} sm item className={styles.progressContainer} role="complementary">
                <Grid container direction="column">
                  <Grid item>
                    <Typography className={styles.totalText}>
                      {t('survey.navigation.position', {
                        questionIndex: currentPosition,
                        questionCount: visibleCount,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item container>
                    <div className={styles.progressBarContainer}>
                      <ProgressBar percent={progress} />
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid xs={12} sm item className={styles.navContainer} role="navigation">
              <Grid
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                spacing={1}
                justifyContent={{ xs: 'end', sm: 'initial' }}
              >
                <SurveyNav
                  questionIndex={currentQuestionIndex}
                  isMissingRequirements={!currentAnswerIsValid}
                  onClickNext={onClickNext}
                  onClickPrev={onClickPrev}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs container alignItems="center" justifyContent={{ xs: 'center', sm: 'initial' }}>
            <Typography variant="body2">
              <a className={styles.link} href={`https://worktango.com`} target="_blank" rel="noreferrer">
                {t('footer.poweredby')}
              </a>
            </Typography>
          </Grid>
        </Grid>
      </footer>
    </div>
  );
};

SurveyPage.propTypes = {
  preview: PropTypes.bool,
};

export default SurveyPage;
